import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    RebelColors,
    RebelButton,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../styles/rebel-experiences';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import { listenToCollection, listenToDocument, updateDocumentProperty } from '../../../utils/firestore-functions';

const RebelVillaBasicPricingTab = ({ villaId }) => {
    const [categories, setCategories] = useState([]);
    const [pricingData, setPricingData] = useState({});
    const [originalPricingData, setOriginalPricingData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedModal, setSavedModal] = useState(false);

    const budgetDB = GetDB();

    // Listen to categories from agenda-block-types
    useEffect(() => {
        const unsubscribeCategories = listenToCollection(budgetDB, "agenda-block-types", (data) => {
            if (data && data.length > 0) {
                const sortedCategories = data.sort((a, b) => {
                    if (a.order !== undefined && b.order !== undefined) {
                        return a.order - b.order;
                    }
                    return (a.name || '').localeCompare(b.name || '');
                });
                setCategories(sortedCategories);
            } else {
                setCategories([]);
            }
            setLoading(false);
        });

        return () => {
            if (unsubscribeCategories) unsubscribeCategories();
        };
    }, [budgetDB]);

    // Listen to villa document for basicViewPricing
    useEffect(() => {
        const unsubscribeVilla = listenToDocument(budgetDB, "rebelVillas", villaId, (villa) => {
            if (villa && villa.basicViewPricing) {
                setPricingData(villa.basicViewPricing);
                setOriginalPricingData(villa.basicViewPricing);
            } else {
                setPricingData({});
                setOriginalPricingData({});
            }
        });

        return () => {
            if (unsubscribeVilla) unsubscribeVilla();
        };
    }, [villaId, budgetDB]);

    const handlePriceChange = (categoryId, subcategory, value) => {
        const numValue = parseFloat(value) || 0;

        setPricingData(prev => ({
            ...prev,
            [categoryId]: {
                ...(prev[categoryId] || {}),
                [subcategory]: numValue
            }
        }));
    };

    const getPrice = (categoryId, subcategory) => {
        return pricingData[categoryId]?.[subcategory] ?? 0;
    };

    const hasSavedPrice = (categoryId, subcategory) => {
        return originalPricingData[categoryId]?.[subcategory] !== undefined;
    };

    const savePricing = async () => {
        setSaving(true);
        try {
            // Build complete pricing object with 0 for any missing subcategory prices
            const completePricingData = {};
            categories.forEach(category => {
                completePricingData[category.id] = {};
                if (category.subcategories && category.subcategories.length > 0) {
                    category.subcategories.forEach(sub => {
                        completePricingData[category.id][sub] = pricingData[category.id]?.[sub] ?? 0;
                    });
                }
            });

            await new Promise((resolve, reject) => {
                updateDocumentProperty(
                    budgetDB,
                    "rebelVillas",
                    villaId,
                    "basicViewPricing",
                    completePricingData,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            setPricingData(completePricingData);
            setOriginalPricingData(completePricingData);
            setSavedModal(true);
        } catch (error) {
            console.error('Error saving pricing:', error);
            alert('Error saving pricing. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <LoadingContainer>
                <p>Loading pricing configuration...</p>
            </LoadingContainer>
        );
    }

    return (
        <React.Fragment>
            <RebelModal isOpen={savedModal} toggle={() => setSavedModal(false)}>
                <RebelModalHeader toggle={() => setSavedModal(false)}>Saved</RebelModalHeader>
                <RebelModalBody>
                    Successfully saved basic view pricing data.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelButton onClick={() => setSavedModal(false)}>
                        Okay
                    </RebelButton>
                </RebelModalFooter>
            </RebelModal>

            <PricingContainer>
                <HeaderSection>
                    <SectionTitle>
                        <RebelIcon icon={['fas', 'tags']} />
                        Basic View Pricing
                    </SectionTitle>
                    <SectionDescription>
                        Configure per-person costs for agenda activities by category and subcategory.
                        These prices will be used when calculating costs in the basic agenda view.
                    </SectionDescription>
                </HeaderSection>

                {categories.length === 0 ? (
                    <EmptyState>
                        <RebelIcon icon={['fas', 'tags']} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
                        <p>No categories found. Please configure categories in the Config tab first.</p>
                    </EmptyState>
                ) : (
                    <CategoryGrid>
                        {categories.map((category) => (
                            <CategoryCard key={category.id}>
                                <CategoryHeader>
                                    <RebelIcon icon={['fas', 'tag']} />
                                    {category.name}
                                </CategoryHeader>
                                {category.description && (
                                    <CategoryDescription>{category.description}</CategoryDescription>
                                )}
                                <SubcategoryList>
                                    {category.subcategories && category.subcategories.length > 0 ? (
                                        category.subcategories.map((sub, index) => (
                                            <SubcategoryRow key={index}>
                                                <SubcategoryName>{sub}</SubcategoryName>
                                                <PriceInputWrapper>
                                                    <PricePrefix>$</PricePrefix>
                                                    <PriceInput
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={getPrice(category.id, sub)}
                                                        onChange={(e) => handlePriceChange(category.id, sub, e.target.value)}
                                                        placeholder="0"
                                                    />
                                                    <PriceSuffix>/person</PriceSuffix>
                                                </PriceInputWrapper>
                                                {!hasSavedPrice(category.id, sub) && (
                                                    <MissingPriceText>No saved price</MissingPriceText>
                                                )}
                                            </SubcategoryRow>
                                        ))
                                    ) : (
                                        <NoSubcategories>
                                            <RebelIcon icon={['fas', 'info-circle']} />
                                            No subcategories defined
                                        </NoSubcategories>
                                    )}
                                </SubcategoryList>
                            </CategoryCard>
                        ))}
                    </CategoryGrid>
                )}

                <SaveButtonContainer>
                    <RebelButton onClick={savePricing} disabled={saving}>
                        <RebelIcon icon={['fas', 'check-circle']} />
                        {saving ? 'Saving...' : 'Save Basic View Pricing'}
                    </RebelButton>
                </SaveButtonContainer>
            </PricingContainer>
        </React.Fragment>
    );
};

export default RebelVillaBasicPricingTab;

const LoadingContainer = styled.div`
    text-align: center;
    padding: 48px;
    color: ${RebelColors.primaryMuted};
`;

const PricingContainer = styled.div`
    padding: 24px;
`;

const HeaderSection = styled.div`
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid ${RebelColors.gold}40;
`;

const SectionTitle = styled.h3`
    color: ${RebelColors.primary};
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        color: ${RebelColors.gold};
    }
`;

const SectionDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: ${RebelColors.primaryMuted};
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 2px dashed ${RebelColors.border};

    p {
        margin-top: 16px;
        font-style: italic;
    }
`;

const CategoryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const CategoryCard = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 12px;
    padding: 20px;
    transition: all 0.3s ease;

    &:hover {
        border-color: ${RebelColors.gold}60;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.1);
    }
`;

const CategoryHeader = styled.h4`
    color: ${RebelColors.primary};
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: ${RebelColors.gold};
        font-size: 14px;
    }
`;

const CategoryDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    margin: 0 0 16px 0;
    line-height: 1.4;
`;

const SubcategoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SubcategoryRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    background: ${RebelColors.background};
    border-radius: 8px;
    border: 1px solid ${RebelColors.border};
`;

const SubcategoryName = styled.span`
    color: ${RebelColors.primary};
    font-size: 14px;
    font-weight: 500;
    flex: 1;
    min-width: 100px;
`;

const PriceInputWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const PricePrefix = styled.span`
    color: ${RebelColors.gold};
    font-size: 14px;
    font-weight: 600;
`;

const PriceInput = styled.input`
    background: ${RebelColors.surfaceLight};
    border: 1px solid ${RebelColors.border};
    color: ${RebelColors.primary};
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 14px;
    width: 80px;
    text-align: right;

    &:focus {
        outline: none;
        border-color: ${RebelColors.gold};
        background: ${RebelColors.surface};
    }

    &::placeholder {
        color: ${RebelColors.primaryMuted};
    }

    /* Hide spinner buttons */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    -moz-appearance: textfield;
`;

const PriceSuffix = styled.span`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
`;

const MissingPriceText = styled.span`
    width: 100%;
    color: ${RebelColors.primaryMuted};
    font-size: 11px;
    font-style: italic;
    text-align: right;
    opacity: 0.7;
`;

const NoSubcategories = styled.div`
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;

    svg {
        font-size: 12px;
    }
`;

const SaveButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    padding-top: 16px;
    border-top: 1px solid ${RebelColors.border};
`;
